import { Location } from '@angular/common';
import { inject } from '@angular/core';
import type { Routes } from '@angular/router';

/**
 * 앱 최초 진입 URL이 인증 가드로 보호된 경로인지 판별한다 — `AppShellRootComponent`의
 * `showBootLoading` 입력을 게이팅할 때, guard 없는 공개 경로(랜딩·로그인·콜백 등)로
 * 진입했다면 각 화면이 자체 로딩 상태를 표시하므로 앱 루트에서 콘텐츠 영역 전체를
 * 막을 필요가 없다.
 *
 * 판별은 최상위 `routes` 배열만 본다(children 내부까지 재귀하지 않는다) — `canActivate`가
 * 자식 라우트 개별에만 걸려 있고 부모 pathless 그룹 자체엔 없는 경우(`sbe-commerce/user-web`의
 * `market/cart`처럼)까지는 잡지 않는다. 이 앱은 애초에 boot-gate가 없었고, 이번 훅의 목적은
 * `/market`·`/developer` 같은 최상위 딥링크의 toolbar 크롬 누락 회귀를 막는 것으로 한정된다
 * (원본 구현 주석 그대로 유지 — 완전한 커버리지는 범위 밖).
 *
 * **Angular Router의 순서 기반 매칭을 재현한다** — 최상위 라우트를 배열 선언 순서대로
 * 순회하며 첫 매치를 쓴다(`':param'` 세그먼트는 와일드카드로 취급). 첫 세그먼트만 보는
 * 단순 비교로는 형제 라우트가 첫 세그먼트를 공유하는 경우(`sbe-commerce/user-web`의
 * `developer/consent`(가드 없음) vs `developer`(가드 있음, children))를 구분하지 못해
 * `/developer/clients`(보호됨)를 공개로 오분류한다(2026-08-11 유닛 테스트로 발견,
 * `sbe-commerce/user-web`의 원본 hand-roll 구현이 이미 이 전체 세그먼트 prefix 매칭으로
 * 그 버그를 고쳐 뒀었다 — 2026-08-13 이 훅으로 승격하며 canonical화했다).
 *
 * `Location.path()`로 경로를 읽는다 — base href를 자동으로 제외한 상대 경로를 돌려줘
 * 서브패스 배포에서도 안전하고(`window.location.pathname` 대신 base href를 수동으로
 * 잘라내는 방식보다 하드닝됨, Gemini 리뷰 지적), `window` 참조가 없어 SSR 도입 시에도
 * 그대로 동작한다. 쿼리스트링을 포함해 반환하므로(해시는 제외) 세그먼트 비교 전에 잘라낸다.
 *
 * 이 lib은 auth lib(`spinoff-platform`/`staff-oidc-auth`)에 의존하지 않는다 — 순수
 * 라우트·URL 판별만 하므로 어떤 인증 체계를 쓰는 앱이든 재사용할 수 있다.
 */
function pathSegments(path: string): readonly string[] {
  return path.split('/').filter(Boolean);
}

// route.path가 실제 진입 세그먼트의 접두사로 매치되는지 — `path: ''`(빈 세그먼트)는
// Angular의 `pathMatch: 'full'` 관례대로 segments가 완전히 비어 있을 때만(정확히 루트일
// 때만) 매치한다. `'**'`(wildcard) 라우트는 항상 다른 구체적 라우트보다 나중에 선언돼
// catch-all 역할만 하므로 boot-loading 판별 대상에서 제외한다.
//
// segments가 routeSegments보다 짧으면(필수 `:param` 세그먼트가 누락된 경우, 예:
// `path: 'orgs/:orgId'`에 `/orgs`로 진입) 매치하지 않는다 — `:param` 세그먼트는
// "아무 값이나 허용"이지 "생략 가능"이 아니다. 이 길이 검사가 없으면 `segments[i]`가
// `undefined`인 인덱스에서도 `:param` 와일드카드가 트리비얼하게 통과해, Angular
// Router라면 애초에 매치하지 않을 불완전한 경로를 잘못 매치된 것으로 오판한다
// (Gemini 리뷰 지적, 2026-08-13).
function matchesTopLevelPath(route: Routes[number], segments: readonly string[]): boolean {
  if (typeof route.path !== 'string' || route.path === '**') return false;
  const routeSegments = pathSegments(route.path);
  if (routeSegments.length === 0) return segments.length === 0;
  if (segments.length < routeSegments.length) return false;
  return routeSegments.every((seg, i) => seg.startsWith(':') || segments[i] === seg);
}

// 최상위 라우트가 redirectTo만 있고 canActivate가 없으면(예: `path: '', redirectTo: 'dashboard'`),
// 그 자체는 guard가 없어도 실제 도착지가 보호돼 있을 수 있다 — 이 경우까지 공개로 분류하면
// 도착지 guard가 auth.init()을 기다리는 동안 스피너 없이 빈 화면이 노출된다(Gemini 리뷰
// 지적, 2026-08-13). `redirectTo`가 가리키는 최상위 라우트를 재귀적으로 따라가 실제
// canActivate 여부를 판별한다. `redirectTo`는 이 저장소 라우트에서 전부 순수 문자열이라
// (`UrlMatchResult`/함수형 `redirectTo`는 아직 안 씀) 문자열 케이스만 다룬다. 순환·과도한
// 체인은 hop 상한으로 막고, 상한 도달 시 공개로 취급한다(보수적 기본값 — 스피너를
// 못 띄우는 쪽이지, 공개 화면에 스피너를 잘못 씌우는 쪽보다 안전하다).
const MAX_REDIRECT_HOPS = 10;

function isRoutePublic(route: Routes[number], routes: Routes, segments: readonly string[], hops: number, rootRoutes: Routes = routes): boolean {
  if (route.canActivate) return false;
  if (typeof route.redirectTo === 'string' && hops < MAX_REDIRECT_HOPS) {
    const redirectSegments = pathSegments(route.redirectTo);
    // 절대 경로 redirectTo(`/x`)는 자식 안에서도 루트 기준이라 최상위 라우트에서, 상대 경로(`x`)는 같은 레벨의 형제 라우트에서 찾는다.
    const searchRoutes = route.redirectTo.startsWith('/') ? rootRoutes : routes;
    const target = searchRoutes.find((r) => matchesTopLevelPath(r, redirectSegments));
    if (target) return isRoutePublic(target, searchRoutes, redirectSegments, hops + 1, rootRoutes);
  }
  // 이 라우트 자체에는 guard가 없어도 진입 경로가 가리키는 자식에 guard가 있을 수 있다 — `orgs/:orgId`처럼 로그인 같은 미인증 진입점(형제)과 인증 그룹(경로 없는 자식 그룹, `canActivate`)이 한 부모 아래 있는 구조.
  // 진입 세그먼트와 처음 매치되는 자식으로 내려가 판별한다. 매치되는 자식이 없으면 이 라우트의 판정(공개)을 그대로 쓴다.
  if (route.children?.length && typeof route.path === 'string') {
    const remaining = segments.slice(pathSegments(route.path).length);
    const child = route.children.find((c) => matchesChildPath(c, remaining));
    if (child) return isRoutePublic(child, route.children, remaining, hops, rootRoutes);
  }
  return true;
}

// 자식 라우트가 남은 진입 세그먼트와 매치되는지 — 경로 없는 자식(`path: ''`)은 `pathMatch: 'full'`이면 남은 세그먼트가 없을 때만, 아니면(경로 없는 그룹) 남은 세그먼트가 몇 개든 매치한다(Angular 매칭과 같다).
function matchesChildPath(route: Routes[number], segments: readonly string[]): boolean {
  if (typeof route.path !== 'string' || route.path === '**') return false;
  const routeSegments = pathSegments(route.path);
  if (routeSegments.length === 0) {
    if (route.pathMatch === 'full') return segments.length === 0;
    // 경로 없는 그룹은 자기 자식 중 하나가 남은 세그먼트와 매치될 때만 매치한다 — Angular는 자식이 매치되지 않으면 되돌아가 다음 형제를 시도한다.
    return route.children?.length ? route.children.some((c) => matchesChildPath(c, segments)) : true;
  }
  return matchesTopLevelPath(route, segments);
}

/**
 * 알려진 한계 — `pathMatch: 'full'` 없는 `path: ''` pathless 그룹(예: `{ path: '',
 * canActivate: [guard], children: [...] }`)은 Angular Router라면 하위 세그먼트가
 * 몇 개든 접두사로 매치해 children으로 위임하지만, `matchesTopLevelPath`는 `path: ''`를
 * `segments.length === 0`(정확히 루트)일 때만 매치시킨다. 그래서 그런 그룹의 자식 경로
 * (예: `/staff`)로 진입하면 이 최상위 라우트 자체는 매치되지 않고, 아래 "매칭되는 라우트가
 * 없으면 보호됨으로 간주" 폴백에 걸려 결과적으로 여전히 `true`가 나온다 — 다만 이건 실제
 * 매칭이 아니라 우연히 fallback이 맞아떨어진 것이다. 이런 그룹이 **공개**(canActivate 없음)
 * 라면 같은 폴백이 `true`(보호됨)로 잘못 나와 불필요한 스피너가 잠깐 뜬다(스피너를 아예 못
 * 띄우는 반대 방향 실패보다 안전한 쪽 오류이긴 하다). 현재 이 훅을 쓰는 어떤 앱도 이런
 * 구성(공개 pathless 최상위 그룹)을 갖고 있지 않아 실제로 잘못된 결과를 내는 사례는 없다
 * (코드 리뷰 지적, 2026-08-13) — children까지 재귀하는 완전한 매칭은 위 "판별은 최상위
 * routes 배열만 본다" 스코프 제한과 함께 향후 필요해지면 별도로 확장한다.
 */
export function injectProtectedEntry(routes: Routes): boolean {
  const location = inject(Location);

  const segments = pathSegments(location.path().split('?')[0]);
  const matched = routes.find((route) => matchesTopLevelPath(route, segments));
  if (!matched) return true; // 매칭되는 최상위 라우트가 없음(404 등) — 보수적으로 보호됨 취급.

  return !isRoutePublic(matched, routes, segments, 0);
}
